/* BASIN Design Contest entry form.
   Design files are large, so a file never goes through Basin's API: the page asks the API
   for a one-time upload form, sends the file straight to storage, then submits the entry.
   The API refuses the entry unless the file really arrived. One form holds up to two
   designs; each design is its own entry. */
(function () {
  var TYPES = ['png', 'pdf', 'svg', 'ai', 'eps'];
  var MAX_BYTES = 50 * 1024 * 1024;

  function extension(name) {
    var dot = String(name || '').lastIndexOf('.');
    return dot < 0 ? '' : String(name).slice(dot + 1).toLowerCase();
  }

  function fileProblem(label, file, help) {
    if (TYPES.indexOf(extension(file.name)) < 0) return label + ' must be a PNG, PDF, SVG, AI, or EPS file.';
    if (!file.size) return label + ' is an empty file.';
    if (file.size > MAX_BYTES) return label + ' is larger than 50 MB. Email it to ' + help + ' instead.';
    return '';
  }

  // Design 2 counts only once something is put in it.
  function used(design) {
    return !!(design && (design.front || design.back || (design.text || '').trim()));
  }

  // Everything wrong with the form, in the order the fields appear.
  // `received` marks designs that are already in and need no second look.
  function problems(values, designs, help, received) {
    var found = [];
    received = received || [];
    if (!values.first_name) found.push('Enter your first name.');
    if (!values.last_name) found.push('Enter your last name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email || '')) found.push('Enter a valid email address.');
    if (!values.instagram) found.push('Enter your Instagram handle.');
    designs.forEach(function (design, index) {
      var name = 'Design ' + (index + 1);
      if (received[index] || (index > 0 && !used(design))) return;
      if (!design.front) found.push(index ? name + ' needs a front file, or clear it to send only Design 1.' : 'Choose the front file for Design 1.');
      else if (fileProblem(name + ' front', design.front, help)) found.push(fileProblem(name + ' front', design.front, help));
      if (design.back && fileProblem(name + ' back', design.back, help)) found.push(fileProblem(name + ' back', design.back, help));
    });
    if (!values.agree_rules) found.push('Check the box to agree to the Official Rules.');
    return found;
  }

  // "Title & notes" is one box: its first line is the title, the rest is notes.
  function titleAndNotes(text) {
    var lines = String(text || '').replace(/\r/g, '').trim().split('\n');
    return { title: lines[0].trim().slice(0, 200), notes: lines.slice(1).join('\n').trim().slice(0, 2000) };
  }

  // True from the closing time on. A missing or unreadable time never closes the form.
  function closed(closesAt, now) {
    var end = Date.parse(closesAt || '');
    return !isNaN(end) && now.getTime() >= end;
  }

  function fallback(help) {
    return 'Something went wrong sending your entry. Please try again, or email your design to ' + help + '.';
  }

  // Ask the API, and turn any refusal into the sentence it sent.
  function ask(fetcher, url, body, help) {
    return fetcher(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (response) {
        return response.json().catch(function () { return null; }).then(function (answer) {
          if (response.ok) return answer;
          throw new Error((answer && answer.detail && answer.detail.message) || fallback(help));
        });
      }, function () { throw new Error(fallback(help)); });
  }

  function upload(deps, side, file) {
    return ask(deps.fetch, deps.api + '/uploads', { side: side, filename: file.name, size: file.size }, deps.help)
      .then(function (form) {
        var body = new deps.FormData();
        Object.keys(form.fields).forEach(function (name) { body.append(name, form.fields[name]); });
        body.append('file', file);   // storage requires the file to be the last field
        // Storage does not answer cross-site pages, so the reply is unreadable here.
        // The entry step is what proves the file arrived.
        return deps.fetch(form.url, { method: 'POST', body: body, mode: 'no-cors' })
          .then(function () { return form.key; }, function () { throw new Error(fallback(deps.help)); });
      });
  }

  function sendOne(deps, values, design, name) {
    var step = deps.onStep || function () {};
    var words = titleAndNotes(design.text);
    step(name + ': uploading the front…');
    return upload(deps, 'front', design.front).then(function (frontKey) {
      if (!design.back) return [frontKey, ''];
      step(name + ': uploading the back…');
      return upload(deps, 'back', design.back).then(function (backKey) { return [frontKey, backKey]; });
    }).then(function (keys) {
      step(name + ': sending your entry…');
      return ask(deps.fetch, deps.api + '/entries', {
        first_name: values.first_name, last_name: values.last_name, email: values.email,
        instagram: values.instagram || '', title: words.title, notes: words.notes,
        front_key: keys[0], front_name: design.front.name,
        back_key: keys[1], back_name: design.back ? design.back.name : '',
        agree_rules: true, website: values.website || '',
      }, deps.help);
    });
  }

  // deps: { api, help, fetch, FormData, onStep }. `received` remembers which designs are
  // already in, so trying again after a failure never enters the same design twice.
  function send(deps, values, designs, received) {
    var chain = Promise.resolve();
    designs.forEach(function (design, index) {
      if (received[index] || (index > 0 && !used(design))) return;
      chain = chain.then(function () {
        return sendOne(deps, values, design, 'Design ' + (index + 1));
      }).then(function () { received[index] = true; }, function (error) {
        if (index > 0 && received[0]) throw new Error('Design 1 is in. Design 2 did not go through: ' + error.message);
        throw error;
      });
    });
    return chain;
  }

  function mount(root) {
    if (closed(root.getAttribute('data-closes'), new Date())) {
      root.setAttribute('data-dc-state', 'closed');
      return;
    }
    var form = root.querySelector('form');
    var errors = root.querySelector('[data-dc="errors"]');
    var status = root.querySelector('[data-dc="status"]');
    var button = form.querySelector('button[type="submit"]');
    var help = root.getAttribute('data-help');
    var received = [false, false];
    var field = function (name) { return form.elements[name]; };
    var show = function (messages) {
      errors.textContent = '';
      messages.forEach(function (message) {
        var item = document.createElement('li');
        item.textContent = message;
        errors.appendChild(item);
      });
      errors.hidden = !messages.length;
      if (messages.length) errors.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };
    // Each upload box names the file that was picked.
    Array.prototype.forEach.call(form.querySelectorAll('input[type="file"]'), function (input) {
      input.addEventListener('change', function () {
        var picked = input.parentNode.querySelector('[data-dc="picked"]');
        picked.textContent = input.files[0] ? input.files[0].name : '';
        input.parentNode.classList.toggle('is-picked', !!input.files[0]);
      });
    });
    var copy = root.querySelector('[data-dc="copy"]');
    if (copy) copy.addEventListener('click', function () {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(help).then(function () { copy.textContent = 'Copied'; });
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var values = {};
      ['first_name', 'last_name', 'email', 'instagram', 'website'].forEach(function (name) {
        values[name] = field(name).value.trim();
      });
      values.agree_rules = field('agree_rules').checked;
      var designs = [1, 2].map(function (n) {
        return { text: field('text_' + n).value, front: field('front_' + n).files[0], back: field('back_' + n).files[0] };
      });
      var found = problems(values, designs, help, received);
      show(found);
      if (found.length) return;
      button.disabled = true;
      status.hidden = false;
      send({ api: root.getAttribute('data-api'), help: help, fetch: window.fetch.bind(window), FormData: window.FormData,
             onStep: function (text) { status.textContent = text; } }, values, designs, received)
        .then(function () {
          root.setAttribute('data-dc-state', 'done');
          root.querySelector('[data-dc="done"]').scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, function (error) {
          status.hidden = true;
          button.disabled = false;
          if (received[0]) root.querySelector('[data-dc="design-1"]').disabled = true;
          show([error.message]);
        });
    });
  }

  window.BasinDesignContest = { problems: problems, send: send, closed: closed, titleAndNotes: titleAndNotes, extension: extension };

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-design-contest]'), mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
